import { useState } from 'react'
import { useSnapshot } from 'valtio'
import styled from 'styled-components'
import useApi from '../../../hooks/useApi'
import useTranslation from '../../../hooks/useTranslation'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { documentStore } from '../../../state/documentStore'
import invalidateListQueries from '../../../utils/invalidateListQueries'
import { Icon_Folder } from '../../../styles/icons'
import * as BsIcons from 'react-icons/bs'

const Tree = styled.div`
  padding: 0.75rem 0.5rem;
`

const FolderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.35rem 0.5rem;
  padding-left: ${({ $depth }) => `${($depth || 0) * 1.25 + 0.5}rem`};
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  color: ${({ theme, $selected, $isDragOver }) =>
    $isDragOver ? (theme.color?.primary || '#2563eb') :
    $selected ? (theme.color?.primary || '#2563eb') : (theme.font?.label || '#374151')};
  background: ${({ theme, $selected, $isDragOver }) =>
    $isDragOver ? (theme.color?.primaryLight || '#dbeafe') :
    $selected ? (theme.dropdown?.selectedBackground || '#eff6ff') : 'transparent'};
  font-weight: ${({ $selected, $isDragOver }) => ($selected || $isDragOver) ? 600 : 400};
  outline: ${({ theme, $isDragOver }) => $isDragOver ? `2px dashed ${theme.color?.primary || '#2563eb'}` : 'none'};

  &:hover {
    background: ${({ theme, $selected, $isDragOver }) =>
      $isDragOver ? (theme.color?.primaryLight || '#dbeafe') :
      $selected
        ? (theme.dropdown?.selectedBackground || '#eff6ff')
        : (theme.dropdown?.hoverBackground || theme.area?.highBg || '#f3f4f6')
    };
  }

  svg {
    font-size: 1rem;
    flex-shrink: 0;
    opacity: 0.65;
  }
`

const AllItem = styled(FolderRow)`
  font-weight: ${({ $selected }) => $selected ? 600 : 500};
  margin-bottom: 0.35rem;
  border-bottom: 1px solid ${({ theme }) => theme.border?.default || '#e5e7eb'};
  padding-bottom: 0.5rem;
  border-radius: 0;
`

const Chevron = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  width: 0.875rem;
  transition: transform 0.15s ease;
  transform: ${({ $expanded }) => $expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
  font-size: 0.65rem;
  color: ${({ theme }) => theme.font?.subtle || '#9ca3af'};
`

const ChevronGap = styled.span`
  display: inline-block;
  width: 0.875rem;
  flex-shrink: 0;
`

const ROOT = '__root__'

const FolderTree = () => {
  const snap = useSnapshot(documentStore)
  const { getMe, putMe } = useApi()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState(new Set())
  const [dragOverId, setDragOverId] = useState(null)

  const handleDrop = async (e, folder) => {
    e.preventDefault()
    setDragOverId(null)
    const raw = e.dataTransfer.getData('text/plain')
    if (!raw) return
    let doc
    try { doc = JSON.parse(raw) } catch { return }
    if (!doc.id || doc.folderId === folder.id) return
    await putMe('document', { folderId: folder.id, folderName: folder.name, folderPath: folder.path }, doc.id)
    await invalidateListQueries('document', queryClient)
  }

  const { data } = useQuery({
    queryKey: ['folder', 'tree'],
    queryFn: () => getMe('folder?noPagination=true&orderBy=path'),
    staleTime: 30000
  })

  const folders = data?.data ?? []

  // Build parent → children map from flat list
  const childrenOf = {}
  folders.forEach(f => {
    const pid = f.parentFolderId || ROOT
    if (!childrenOf[pid]) childrenOf[pid] = []
    childrenOf[pid].push(f)
  })

  const toggle = (id) => setExpanded(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const handleSelect = (folder) => {
    documentStore.selectedFolderId = folder.id
    documentStore.selectedFolderName = folder.name
    documentStore.selectedFolderPath = folder.path || null
  }

  const handleAll = () => {
    documentStore.selectedFolderId = null
    documentStore.selectedFolderName = null
  }

  const renderFolder = (folder, depth) => {
    const { id, name } = folder
    const children = childrenOf[id] || []
    const hasChildren = children.length > 0
    const isExpanded = expanded.has(id)
    const isSelected = snap.selectedFolderId === id

    return (
      <div key={id}>
        <FolderRow
          $depth={depth}
          $selected={isSelected}
          $isDragOver={dragOverId === id}
          onClick={() => {
            handleSelect(folder)
            if (hasChildren) toggle(id)
          }}
          onDragEnter={(e) => { e.preventDefault(); setDragOverId(id) }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => handleDrop(e, folder)}
        >
          {hasChildren
            ? <Chevron $expanded={isExpanded}><BsIcons.BsChevronRight /></Chevron>
            : <ChevronGap />
          }
          {Icon_Folder}
          {name}
        </FolderRow>
        {isExpanded && children.map(child => renderFolder(child, depth + 1))}
      </div>
    )
  }

  const roots = childrenOf[ROOT] || []

  return (
    <Tree onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverId(null) }}>
      <AllItem
        $depth={0}
        $selected={!snap.selectedFolderId}
        onClick={handleAll}
      >
        {t('document.folder.all')}
      </AllItem>
      {roots.map(folder => renderFolder(folder, 0))}
    </Tree>
  )
}

export default FolderTree
